import ExcelJS from 'exceljs';
import { parsePepUsoEnPtCell, pepUsoEnPtLabel, resolveFabricarSubrecetaDesdePt } from '../data/mockRecetasClassification';
import { cellText } from './classificationExcel';
import { RECETA_EXPORT_COL } from './recetasExcelExport';

export const RECETAS_IMPORT_MODE = {
  CREATE: 'create',
  PEP_USO_BULK_UPDATE: 'pepUsoBulkUpdate',
};

async function loadFirstSheet(file) {
  const buf = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  return wb.worksheets[0] || null;
}

function headerMapFromRow(row) {
  const map = {};
  row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const t = cellText(cell);
    if (t) map[t] = colNumber;
  });
  return map;
}

function getLastUsedRow(sheet) {
  let last = 0;
  sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber > last) last = rowNumber;
  });
  return last;
}

/**
 * Detecta si el archivo es un reporte exportado (actualización masiva de uso en guía PT).
 */
export async function detectRecetasExcelImportMode(file) {
  const sheet = await loadFirstSheet(file);
  if (!sheet) return RECETAS_IMPORT_MODE.CREATE;

  const maxScan = Math.min(30, sheet.rowCount || 30);
  for (let r = 1; r <= maxScan; r++) {
    const map = headerMapFromRow(sheet.getRow(r));
    if (map[RECETA_EXPORT_COL.code] != null && map[RECETA_EXPORT_COL.pepUsoEnPt] != null) {
      return RECETAS_IMPORT_MODE.PEP_USO_BULK_UPDATE;
    }
  }
  return RECETAS_IMPORT_MODE.CREATE;
}

function findExportHeaderRow(sheet) {
  const maxScan = Math.min(30, sheet.rowCount || 30);
  for (let r = 1; r <= maxScan; r++) {
    const map = headerMapFromRow(sheet.getRow(r));
    if (map[RECETA_EXPORT_COL.code] != null && map[RECETA_EXPORT_COL.pepUsoEnPt] != null) {
      return { rowIndex: r, colMap: map };
    }
  }
  return null;
}

function buildRecipeByCode(existingRecipes) {
  const byCode = new Map();
  for (const r of existingRecipes || []) {
    const code = String(r.code ?? '').trim().toUpperCase();
    if (code) byCode.set(code, r);
  }
  return byCode;
}

/**
 * Analiza un Excel exportado para actualizar «Uso en guía PT» por código de receta.
 * @param {File} file
 * @param {{ existingRecipes: object[], globalDefault?: boolean }} ctx
 */
export async function analyzeRecetasPepUsoBulkUpdate(file, ctx = {}) {
  const { existingRecipes = [], globalDefault = false } = ctx;
  const sheet = await loadFirstSheet(file);
  if (!sheet) {
    return { success: false, fatalMessage: 'El archivo no tiene hojas.' };
  }

  const found = findExportHeaderRow(sheet);
  if (!found) {
    return {
      success: false,
      fatalMessage:
        'No se encontraron las columnas «Código receta» y «Uso en guía PT». Exporte el listado con «Exportar Excel», edite esa columna e importe de nuevo.',
    };
  }

  const byCode = buildRecipeByCode(existingRecipes);
  const { rowIndex: headerRow, colMap } = found;
  const codeCol = colMap[RECETA_EXPORT_COL.code];
  const nameCol = colMap[RECETA_EXPORT_COL.name];
  const typeCol = colMap[RECETA_EXPORT_COL.outputType];
  const pepCol = colMap[RECETA_EXPORT_COL.pepUsoEnPt];
  const last = getLastUsedRow(sheet);

  const previewRows = [];
  const pendingByCode = new Map();
  const conflictCodes = new Set();
  const parseErrors = [];
  let carryCode = '';

  for (let r = headerRow + 1; r <= last; r++) {
    const row = sheet.getRow(r);
    const rawCode = cellText(row.getCell(codeCol));
    const rawPep = cellText(row.getCell(pepCol));
    if (rawCode) carryCode = rawCode.trim();
    const code = carryCode;
    if (!code || !rawPep) continue;

    const parsed = parsePepUsoEnPtCell(rawPep);
    if (!parsed.ok) {
      parseErrors.push({ row: r, code, rawPep, error: parsed.error });
      continue;
    }
    if (parsed.skip) continue;

    const codeKey = code.toUpperCase();
    const prev = pendingByCode.get(codeKey);
    if (prev && prev.fabricar_subreceta_desde_pt !== parsed.value) {
      conflictCodes.add(codeKey);
      continue;
    }
    pendingByCode.set(codeKey, {
      code,
      codeKey,
      fabricar_subreceta_desde_pt: parsed.value,
      usoNuevo: parsed.value ? 'Subreceta automática' : 'Guía PEP',
      lastRow: r,
      rawPep,
    });
  }

  for (const err of parseErrors) {
    previewRows.push({
      excelRow: err.row,
      status: 'invalid',
      cols: { codigo: err.code, nombre: '—', tipoSalida: '—', usoNuevo: err.rawPep },
      issue: err.error,
    });
  }

  for (const codeKey of conflictCodes) {
    const pending = pendingByCode.get(codeKey);
    previewRows.push({
      excelRow: pending?.lastRow ?? headerRow + 1,
      status: 'invalid',
      cols: {
        codigo: pending?.code || codeKey,
        nombre: '—',
        tipoSalida: '—',
        usoNuevo: pending?.usoNuevo || '—',
      },
      issue: `El código «${pending?.code || codeKey}» tiene valores distintos en varias filas; unifique «Uso en guía PT».`,
    });
    pendingByCode.delete(codeKey);
  }

  const validUpdates = [];

  for (const pending of pendingByCode.values()) {
    const existing = byCode.get(pending.codeKey);
    const cols = {
      codigo: pending.code,
      nombre: existing?.name || '—',
      tipoSalida: existing?.output_product_type === 'producto_en_proceso' ? 'Producto en proceso' : 'Producto terminado',
      usoNuevo: pending.usoNuevo,
    };

    if (!existing) {
      previewRows.push({
        excelRow: pending.lastRow,
        status: 'invalid',
        cols,
        issue: `No existe una receta con código «${pending.code}» en el sistema.`,
      });
      continue;
    }

    if (existing.output_product_type !== 'producto_en_proceso') {
      previewRows.push({
        excelRow: pending.lastRow,
        status: 'skipped',
        cols: { ...cols, nombre: existing.name, tipoSalida: 'Producto terminado', usoActual: '—' },
        issue: 'Solo aplica a recetas «Producto en proceso»; se omite.',
      });
      continue;
    }

    const usoActual = pepUsoEnPtLabel(existing, globalDefault);
    const currentEffective = resolveFabricarSubrecetaDesdePt(
      existing.fabricar_subreceta_desde_pt,
      globalDefault,
    );

    if (currentEffective === pending.fabricar_subreceta_desde_pt) {
      previewRows.push({
        excelRow: pending.lastRow,
        status: 'skipped',
        cols: { ...cols, nombre: existing.name, usoActual, usoNuevo: pending.usoNuevo },
        issue: 'Ya tiene ese valor; sin cambio.',
      });
      continue;
    }

    validUpdates.push({
      recipeId: existing.id,
      code: existing.code,
      name: existing.name,
      fabricar_subreceta_desde_pt: pending.fabricar_subreceta_desde_pt,
      firstRow: pending.lastRow,
    });

    previewRows.push({
      excelRow: pending.lastRow,
      status: 'valid',
      cols: { ...cols, nombre: existing.name, usoActual, usoNuevo: pending.usoNuevo },
      issue: '',
    });
  }

  previewRows.sort((a, b) => a.excelRow - b.excelRow);
  validUpdates.sort((a, b) => a.firstRow - b.firstRow);
  const invalidCount = previewRows.filter((x) => x.status === 'invalid').length;
  const skippedCount = previewRows.filter((x) => x.status === 'skipped').length;

  if (validUpdates.length === 0 && invalidCount === 0) {
    return {
      success: false,
      fatalMessage:
        'No hay cambios para aplicar. Edite la columna «Uso en guía PT» en recetas PEP (Subreceta automática o Guía PEP).',
    };
  }

  return {
    success: true,
    mode: RECETAS_IMPORT_MODE.PEP_USO_BULK_UPDATE,
    previewRows,
    validUpdates,
    updateCount: validUpdates.length,
    invalidCount,
    skippedCount,
    recipeCount: validUpdates.length,
  };
}

/**
 * @param {Array<{ recipeId: number, fabricar_subreceta_desde_pt: boolean }>} validUpdates
 */
export async function executeRecetasPepUsoBulkUpdate(validUpdates, svc) {
  const result = { updated: 0, errors: [] };
  for (const row of validUpdates || []) {
    try {
      const res = await svc.update(row.recipeId, {
        fabricar_subreceta_desde_pt: row.fabricar_subreceta_desde_pt,
      });
      if (res.success) {
        result.updated += 1;
      } else {
        result.errors.push({
          name: row.name || row.code,
          row: row.firstRow,
          message: res.message || 'Respuesta sin éxito',
        });
      }
    } catch (e) {
      const msg =
        e.response?.data?.message ||
        (e.response?.data?.errors && Object.values(e.response.data.errors).flat().join(' ')) ||
        e.message ||
        'Error al actualizar';
      result.errors.push({
        name: row.name || row.code,
        row: row.firstRow,
        message: msg,
      });
    }
  }
  return result;
}

export function summarizeRecetasPepUsoBulkUpdate(exec) {
  const { updated = 0, errors = [] } = exec || {};
  if (!errors.length) return `Se actualizaron ${updated} receta(s) PEP.`;
  const errPart = errors.map((e) => `«${e.name}»: ${e.message}`).join(' · ');
  return `Actualizadas: ${updated}. Errores: ${errors.length}. ${errPart}`;
}
